import { isUpcomingPeriod, useReviewPeriodLock, useReviewPeriodLocked, periodQuery } from '../workspace/reviewPeriod';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAbfCopilotContext } from '../copilot/AbfCopilotLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  AlertTriangle, CalendarDays, Check, CircleDot,
  Keyboard, RefreshCw, Upload,
} from 'lucide-react';

import { Layout, Note, Readable, SmallButton, TaskButton, TaskRail, Work }
  from '../onboarding/review/contractReviewStyles';
import { Button, Card, CardHead, FieldLabel, INK, Input, Muted } from '../onboarding/review/reviewStyles';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { ActionLocalFeedback, type ActionFeedbackState } from '../components/ActionFeedback';
import { replaceShorthandOnBlur } from '../../../utils/amountUtils';
import { fmtDate, fmtDateTime, fmtMoney, plural, pluralWord } from '../presentation/format';
import {
  apiErrorMessage, attestPeriod, bookLenderCash, countersignPeriod,
  dispositionCase, refreshCollectionsPeriod, getSignoffReadiness, evaluateAlerts, getCollectionsWorkspace, getDispositionTypes, getFormats,
  finalisePeriodWaterfall, getLoanDrillDown, getWaterfallNodes,
  mapWaterfallNode, runCashControl, runPeriodWaterfall,
  getBankLines, classifyBankLine, dropEvidence, getMatching,
  projectExpectedCollections, projectPeriodWaterfall,
  recordPayingAgentReport, recordServicerReport, restatePeriod, reverseLenderCash,
  waiveDeliverable,
  withdrawBankFile,
  type AccountRow, type BankLine, type CollectionsPeriodChip, type DispositionType,
  type LoanDrillDown, type WaterfallNode,
  type CollectionsWorkspace as Workspace, type Deliverable, type ReconcilingItem,
} from './collectionsApi';
import { CollectionsGuidance } from './CollectionsGuidance';
import CollectionsCloseout, { CloseDecisionReview } from './CollectionsCloseout';
import CollectionsAccounts from './CollectionsAccounts';
import CollectionsDeliverables from './CollectionsDeliverables';
import { useAuth } from '../../../contexts/AuthContext';
import { CashStory, CasesWorkbench, CaseEvidence, CollectionsDrawer } from './CollectionsExperience';
import CollectionsFileReview from './CollectionsFileReview';
import CollectionsHandoff from './CollectionsHandoff';
import CollectionsDocumentIntake from './CollectionsDocumentIntake';
import { rememberCollectionsPeriod, rememberedCollectionsPeriod } from './collectionsPeriodMemory';

/**
 * The collections close, one period at a time (FRD §12).
 *
 * <p>Opens on what needs attention: the period rail with a derived stage, the
 * calendar the period actually runs on, the account map, what is still owed, the
 * three-way statement and the five steps that close it. Every date on this
 * screen comes from the contract calendar, and the business date is shown beside
 * the wall clock so "why is this not due yet" never needs asking.
 */

interface Props {
  facilityId: number;
  currency?: string | null;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55;
  h2, h3, p { margin: 0; }
  h2 { font-size: 17px; } h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;

const ContextBar = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px 24px; align-items: center;
  margin-bottom: 18px; padding: 7px 0 14px; border-bottom: 1px solid ${INK.line};
  > div { display: flex; align-items: baseline; flex-wrap: wrap; gap: 7px; }
  span { color: ${INK.muted}; font-size: 12px; }
  strong { color: ${INK.strong}; font-size: 13px; font-weight: 600; }
  small { display: none; }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin-bottom: 16px;
  > div { padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 750;
    letter-spacing: .08em; text-transform: uppercase; }
  strong { display: block; margin-top: 4px; font-size: 18px; letter-spacing: -.01em; color: ${INK.strong}; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 3px; }
`;

const CalendarStrip = styled.ol`
  display: grid; grid-template-columns: repeat(5,minmax(125px,1fr)); list-style: none; padding: 0;
  margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: linear-gradient(110deg,#132238,#0c1524); overflow-x: auto;
  li { padding: 12px 15px; border-top: 2px solid transparent; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-state='TODAY'] { border-top-color: ${INK.accent}; background: #15314a; }
  span { display: block; color: ${INK.faint}; font-size: 10px; font-weight: 650;
    letter-spacing: .055em; text-transform: uppercase; white-space: nowrap; }
  strong { display: block; color: ${INK.strong}; font-size: 14px; font-weight: 600; margin-top: 6px; }
  small { display: block; color: ${INK.muted}; font-size: 11px; margin-top: 4px; }
  li[data-state='TODAY'] small { color: ${INK.accent}; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums;
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em;
    text-transform: uppercase; }
  td.num, th.num { text-align: right; }
  tr[data-tone='break'] td { background: rgba(248,113,113,.07); }
  tr[data-tone='timing'] td { background: rgba(251,191,36,.06); }
  small { display: block; color: ${INK.muted}; font-size: 11.5px; margin-top: 3px; max-width: 78ch; }
  .tag { display: inline-block; margin-right: 8px; padding: 1px 6px; border-radius: 4px;
    font-size: 10px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase;
    color: ${INK.faint}; border: 1px solid ${INK.line}; vertical-align: 1px; }
  tr[data-tone='break'] .tag { color: #f8a5a5; border-color: rgba(248,113,113,.45); }
  tr[data-tone='timing'] .tag { color: #f5c76a; border-color: rgba(251,191,36,.45); }
  .node { color: ${INK.strong}; font-weight: 650; }
`;

const Rail = styled.ol`
  list-style: none; margin: 0; padding: 0; display: grid; gap: 8px;
  li { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 10px; align-items: start;
    padding: 10px 12px; border: 1px solid ${INK.line}; border-radius: 9px; background: #0b1524; }
  li[data-state='DONE'] { border-color: #376e60; }
  li[data-state='BLOCKED'] { border-color: rgba(248,113,113,.42); }
  li[data-state='READY'] { border-color: ${INK.accent}; }
  strong { font-size: 13px; color: ${INK.strong}; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
`;

/** The same five-dash progress the Collateral tab's period rail uses. */
const StageLine = styled.ol`
  display: flex; gap: 4px; list-style: none; padding: 0; margin: 6px 0 0;
  li { width: 14px; height: 6px; border-radius: 3px; background: #26364a; }
  li[data-state='DONE'] { background: #4fd1a5; }
  li[data-state='OPEN'] { background: ${INK.accent}; }
  li[data-state='BLOCKED'] { background: #f0a24a; }
`;

/** In-tab sub-navigation, as the Collateral tab does it. */
const Navigation = styled.nav`
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  margin: 0 0 16px; padding: 8px;
  border: 1px solid rgba(148,163,184,.18); border-radius: 12px;
  background: rgba(8,16,30,.8);
  button {
    display: inline-flex; align-items: center; gap: 7px; min-height: 44px;
    padding: 8px 12px; border: 1px solid rgba(148,163,184,.24); border-radius: 8px;
    color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 14px;
  }
  button[aria-pressed='true'] { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.2); }
  button:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
  .context { display: flex; align-items: center; gap: 6px; margin-left: auto; padding: 8px;
    color: #a2b3c9; font-size: 12px; }
  @media(max-width:900px) { .context { margin-left: 0; width: 100%; } }
`;

const Grid2 = styled.div`
  display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 14px;
  @media (max-width: 1050px) { grid-template-columns: 1fr; }
`;

const Formats = styled.div`
  margin: 10px 0 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: #0b1524;
  details + details { border-top: 1px solid ${INK.line}; }
  summary { padding: 9px 12px; cursor: pointer; color: #cbd5e1; font-size: 12.5px; font-weight: 700; }
  p { margin: 0; padding: 0 12px 9px; color: ${INK.muted}; font-size: 12px; }
  strong { color: #cbd5e1; font-weight: 700; }
`;

const LinkButton = styled.button`
  background: none; border: 0; padding: 0; color: #7dd3fc; cursor: pointer;
  font: inherit; font-weight: 650; text-align: left;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 2px; }
`;

const Field = styled.div`display: grid; gap: 5px; margin-bottom: 10px;`;
const Select = styled.select`
  background: #0b1524; color: ${INK.body}; border: 1px solid ${INK.lineStrong};
  border-radius: 8px; padding: 9px 10px; font-size: 13px; min-height: 40px;
`;
const TextArea = styled.textarea`
  background: #0b1524; color: ${INK.body}; border: 1px solid ${INK.lineStrong};
  border-radius: 8px; padding: 9px 10px; font-size: 13px; min-height: 72px; resize: vertical;
`;
const Drop = styled.label<{ $over: boolean }>`
  display: grid; place-items: center; gap: 6px; padding: 22px; cursor: pointer;
  border: 1.5px dashed ${p => (p.$over ? INK.accent : INK.lineStrong)};
  border-radius: 10px; background: ${p => (p.$over ? 'rgba(56,189,248,.08)' : '#0b1524')};
  color: ${INK.muted}; font-size: 12.5px; text-align: center;
  input { display: none; }
`;

// Collections cash is bank money, so the shared money style keeps its cents here.
const money = (value: number | string | null | undefined, currency = 'USD') => fmtMoney(value, currency, 2);

/**
 * The server's state words, in the workspace's one chip vocabulary (FR-0.1):
 * an icon plus a word, never colour alone. Every state this tab can render is
 * listed, and an unlisted one falls back to neutral with its raw value rather
 * than crashing the page.
 */
const CHIP: Record<string, { tone: StatusTone; label: string }> = {
  // close-out rail
  DONE: { tone: 'done', label: 'Done' },
  READY: { tone: 'progress', label: 'Ready' },
  WAITING: { tone: 'neutral', label: 'Waiting' },
  BLOCKED: { tone: 'critical', label: 'Blocked' },
  // deliverables
  RECEIVED: { tone: 'done', label: 'Received' },
  EXPECTED: { tone: 'progress', label: 'Expected' },
  LATE: { tone: 'critical', label: 'Late' },
  WAIVED: { tone: 'neutral', label: 'Waived' },
  NOT_REQUIRED: { tone: 'neutral', label: 'Not required' },
  // account feed
  CURRENT: { tone: 'done', label: 'Current' },
  STALE: { tone: 'warning', label: 'Stale' },
  NO_STATEMENTS: { tone: 'neutral', label: 'No statements' },
  NO_BALANCES: { tone: 'neutral', label: 'No balances' },
  // reconciling items
  OPEN: { tone: 'critical', label: 'Open' },
  CARRIED: { tone: 'warning', label: 'Carried' },
  CLEARED: { tone: 'done', label: 'Cleared' },
  DISPOSITIONED: { tone: 'done', label: 'Dispositioned' },
  EXPLAINED: { tone: 'done', label: 'Explained' },
  PENDING_APPROVAL: { tone: 'progress', label: 'Pending approval' },
  SUPERSEDED: { tone: 'neutral', label: 'Superseded' },
  // waterfall against the paying agent
  MATCHED: { tone: 'done', label: 'Matched' },
  VARIANCE: { tone: 'critical', label: 'Variance' },
  NOT_ON_STATEMENT: { tone: 'warning', label: 'Not on the statement' },
  NOT_IN_WATERFALL: { tone: 'warning', label: 'Not in the waterfall' },
  UNMAPPED_LINE: { tone: 'warning', label: 'Unmapped line' },
};

const Chip: React.FC<{ state?: string | null }> = ({ state }) => {
  if (!state) return null;
  const chip = CHIP[state] ?? { tone: 'neutral' as StatusTone, label: state.replace(/_/g, ' ').toLowerCase() };
  return <StatusChip tone={chip.tone} label={chip.label} />;
};

const shortDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * A server sentence that carries a raw timestamp ("Signed off on 2026-10-06
 * 21:44:10.560925 by …") or an ISO date reads like the rest of the tab.
 */
const readableDetail = (text?: string | null) => (text || '')
  .replace(/\b(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:?\d{2})?\b/g,
    (whole, d: string, t: string, z?: string) => fmtDateTime(`${d}T${t}${z || ''}`, whole))
  .replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (whole, iso: string) => fmtDate(iso, whole));

const shortWhen = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** UNAPPLIED_COLLECTION → "Unapplied collection". */
const sentenceCase = (code: string) => {
  const words = code.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A waterfall variance carries its node name in the loans slot; it is a headline, not a drill-down. */
const isWaterfallNode = (item: ReconcilingItem) =>
  item.category === 'MISAPPLIED_PAYMENT' && item.direction === 'WATERFALL_VS_AGENT';

type DashState = 'DONE' | 'OPEN' | 'BLOCKED' | 'IDLE';

/** Five dashes: evidence in, statement produced, differences resolved, cash booked, signed off. */
const progressOf = (chip: CollectionsPeriodChip): DashState[] => {
  const evidence: DashState = chip.deliverablesRequired > 0
      && chip.deliverablesReceived >= chip.deliverablesRequired ? 'DONE'
    : chip.deliverablesLate > 0 ? 'BLOCKED'
    : chip.deliverablesReceived > 0 ? 'OPEN' : 'IDLE';
  const produced: DashState = ['CLOSING', 'BREAKS', 'RECONCILED', 'SIGNED_OFF', 'BOOKED']
    .includes(chip.stage) ? 'DONE' : 'IDLE';
  const resolved: DashState = chip.openBreaks > 0 ? 'BLOCKED'
    : ['RECONCILED', 'SIGNED_OFF', 'BOOKED'].includes(chip.stage) ? 'DONE' : 'IDLE';
  const booked: DashState = ['BOOKED', 'SIGNED_OFF'].includes(chip.stage) ? 'DONE' : 'IDLE';
  const signed: DashState = chip.authorityStatus === 'SIGNED_OFF' ? 'DONE'
    : chip.authorityStatus === 'RESTATING' ? 'OPEN' : 'IDLE';
  return [evidence, produced, resolved, booked, signed];
};

/** Collections finish at sign-off; CLOSED only approves collateral authority. */
const finishedLine = (chip: CollectionsPeriodChip): string | null => {
  if (chip.authorityStatus === 'SIGNED_OFF') {
    const who = [chip.signedOffBy, chip.signedOffChecker].filter(Boolean).join(' · ');
    return `Signed off${who ? ` by ${who}` : ''}${chip.signedOffAt ? ` · ${shortDate(String(chip.signedOffAt).slice(0, 10))}` : ''}`;
  }
  return null;
};

const nextActionOf = (chip: CollectionsPeriodChip): string => {
  const finished = finishedLine(chip);
  if (finished) return finished;
  if (chip.authorityStatus === 'SIGNED_OFF') return 'Signed off';
  if (chip.authorityStatus === 'RESTATING') return 'Restating — close it again';
  if (chip.openBreaks > 0) return `Disposition ${chip.openBreaks} break${chip.openBreaks > 1 ? 's' : ''}`;
  if (chip.stage === 'SCHEDULED') return 'Not started';
  if (chip.deliverablesReceived < chip.deliverablesRequired) {
    return `Collect ${plural(chip.deliverablesRequired - chip.deliverablesReceived, 'more deliverable')}`;
  }
  if (chip.stage === 'AWAITING_DELIVERABLES') return "Produce this period's statement";
  if (chip.stage === 'RECONCILED') return 'Book the lender cash and sign off';
  if (chip.stage === 'BOOKED') return 'Sign off';
  return 'Work this period';
};

const PROGRESS_LABELS = ['Evidence in', 'Statement produced', 'Differences resolved',
  'Lender cash booked', 'Signed off'];

type Task = 'period' | 'accounts' | 'deliverables' | 'statement' | 'close';
const TASKS: Array<{ key: Task; label: string }> = [
  { key: 'period', label: 'Period' },
  { key: 'accounts', label: 'Account map' },
  { key: 'deliverables', label: 'Deliverables' },
  { key: 'statement', label: 'Statement and breaks' },
  { key: 'close', label: 'Close out' },
];

const CollectionsWorkspace: React.FC<Props> = ({ facilityId, currency, userRole, onFeedback }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  useReviewPeriodLock(busy !== null);
  const periodChangeLocked = useReviewPeriodLocked();
  const [task, setTask] = useState<Task>(() => TASKS.find(section => section.key === params.get('section'))?.key || 'period');
  const [evidenceChanged, setEvidenceChanged] = useState(false);
  const [formats, setFormats] = useState<Record<string, any> | null>(null);
  // Served, not typed out here: the dropdown, the server and the column's foreign
  // key all read one table, so they cannot disagree about it (F-S5-046).
  const [dispositions, setDispositions] = useState<DispositionType[]>([]);
  // The contract's own priority of payments. It is what the agent's statement is
  // checked against, so it is also the right source for the lines to key —
  // rather than the comparison, which cannot exist until they are keyed
  // (F-S5-056).
  const [nodes, setNodes] = useState<WaterfallNode[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [evidencePanel, setEvidencePanel] = useState<'files' | 'pdf' | 'servicer' | null>(null);
  const [intakeKind, setIntakeKind] = useState('ANY');
  const [documentType, setDocumentType] = useState<'ABF_SERVICER_REPORT' | 'ABF_PAYING_AGENT_REPORT'>('ABF_SERVICER_REPORT');
  const [documentBusy, setDocumentBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dropAccount, setDropAccount] = useState<number | ''>('');
  const [openItem, setOpenItem] = useState<number | null>(null);
  const [disposition, setDisposition] = useState<string>('');
  const [alsoSiblings, setAlsoSiblings] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [alertRun, setAlertRun] = useState<Record<string, any> | null>(null);
  // The disposition panel opens under a table that can be a dozen rows long, so
  // without this the click looks like it did nothing.
  const dispositionPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (openItem !== null) {
      dispositionPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [openItem]);
  // FR-5.9 — one loan, everything known about it. The identifiers on a difference
  // are the way in; before this they were plain text over a built endpoint.
  const [loan, setLoan] = useState<LoanDrillDown | null>(null);
  const [loanFor, setLoanFor] = useState<string | null>(null);
  const [withdrawFile, setWithdrawFile] = useState<{ id: number; name: string } | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [waiveId, setWaiveId] = useState<number | null>(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [linesFor, setLinesFor] = useState<AccountRow | null>(null);
  const [lines, setLines] = useState<BankLine[] | null>(null);
  const [lineClassifications, setLineClassifications] = useState<string[]>([]);
  const [reclassify, setReclassify] = useState<{ id: number; to: string; reason: string } | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [matching, setMatching] = useState<Record<string, any> | null>(null);
  const [notes, setNotes] = useState('');
  const [readiness, setReadiness] = useState<Record<string, any> | null>(null);
  const [automation, setAutomation] = useState('');
  const [pendingAction, setPendingAction] = useState<'book' | 'finalize' | 'attest' | 'countersign' | null>(null);
  const [actionConfirmed, setActionConfirmed] = useState(false);
  const [actionError, setActionError] = useState('');
  const requestSeq = useRef(0);
  const currentPeriodId = useRef<number | null>(null);
  const mounted = useRef(true);
  const activePeriodRef = useRef<HTMLButtonElement | null>(null);

  // The board's period cells link with ?periodId= (the Reporting tab's name for it); both open that period.
  const periodParam = params.get('period') || params.get('periodId');
  const selectedPeriod = periodParam ? Number(periodParam) : null;
  useAbfCopilotContext({periodId: selectedPeriod || undefined, subview: task});
  const ccy = data?.period?.accountMap?.currency || currency || 'USD';

  const load = useCallback(async (periodId?: number | null) => {
    let reopening = false;
    const seq = ++requestSeq.current;
    try {
      const next = await getCollectionsWorkspace(facilityId, periodId ?? selectedPeriod);
      if (!mounted.current || seq !== requestSeq.current) return;
      if (!periodParam) {
        // F-MK-076: opened without ?period= (back from Document Processing, a link, a reload) —
        // reopen the period the reader was working on, or the one an import just landed on,
        // rather than the business-date period the server picks.
        const remembered = rememberedCollectionsPeriod(facilityId);
        const target = remembered && next.periods.some(p => p.id === remembered) ? remembered : next.selectedPeriodId;
        if (target) {
          const copy = new URLSearchParams(params);
          copy.set('period', String(target));
          setParams(copy, { replace: true });
          // A different period loads on the URL change; showing this one first would flash it.
          if (target !== next.selectedPeriodId) { reopening = true; return; }
        }
      }
      setData(next);
      currentPeriodId.current = next.period?.id || null;
      if (next.period) {
        const r = await getSignoffReadiness(facilityId, next.period.id);
        if (mounted.current && seq === requestSeq.current) setReadiness(r);
      }
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Could not load the collections workspace') });
    } finally {
      if (mounted.current && seq === requestSeq.current && !reopening) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, selectedPeriod, periodParam]);

  useEffect(() => {
    mounted.current = true;
    setLoading(true); setData(null); setReadiness(null); setMatching(null);
    setOpenItem(null); setLoanFor(null); setLinesFor(null); setPendingFiles([]); setPendingAction(null);
    currentPeriodId.current = null;
    setEvidencePanel(null); setWithdrawFile(null); setWaiveId(null);
    load();
    getFormats(facilityId).then(setFormats).catch(() => undefined);
    getDispositionTypes(facilityId).then(setDispositions).catch(() => undefined);
    getWaterfallNodes(facilityId).then(setNodes).catch(() => undefined);
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, selectedPeriod]);

  const act = async (key: string, run: () => Promise<any>, success: string) => {
    const target = currentPeriodId.current;
    setActionError('');
    setBusy(key);
    let result: any;
    try {
      result = await run();
      const refreshBlocked = key === 'refresh-checks' && result?.outcome !== 'REFRESHED';
      onFeedback({ tone: refreshBlocked ? 'warning' : 'success', message: result?.message || success });
      const evidenceMutation = key.startsWith('file:') || key.startsWith('withdraw:') || key.startsWith('waive:')
        || ['servicer', 'servicer-report', 'paying-agent', 'reclassify', 'disposition', 'statement', 'allocate'].includes(key);
      if (evidenceMutation && target) {
        setAutomation('Updating matching, statement and independent cash verification…');
        try {
          const refreshed = await refreshCollectionsPeriod(facilityId, target);
          if (target === currentPeriodId.current) { setAutomation(refreshed.message); setEvidenceChanged(refreshed.outcome !== 'REFRESHED'); }
        } catch (error) {
          if (target === currentPeriodId.current) { setEvidenceChanged(true); setAutomation('Evidence is saved, but dependent checks could not finish. Retry refresh; do not re-upload or assume a clean control.'); }
          onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Evidence saved; refresh needs attention') });
        }
      }
      return result;
    } catch (error) {
      const message = apiErrorMessage(error, 'The server refused that');
      setActionError(message);
      onFeedback({ tone: 'error', message });
      return null;
    } finally {
      // Even a partially completed external workflow must be re-read, never left looking current.
      if (target === currentPeriodId.current) {
        await load(target);
        try { const value = target ? await getMatching(facilityId, target) : null; if (target === currentPeriodId.current) setMatching(value); } catch { if (target === currentPeriodId.current) setMatching(null); }
      }
      setBusy(null);
    }
  };

  const selectPeriod = (id: number) => {
    if (busy !== null || id === data?.period?.id) return;
    setLoading(true);
    setEvidenceChanged(false); setAutomation(''); setOpenItem(null); setLoanFor(null); setLinesFor(null); setForm({}); setNotes(''); setReadiness(null); setPendingAction(null); setPendingFiles([]); currentPeriodId.current = null;
    const copy = periodQuery(params, id);
    setParams(copy);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || !data?.period || busy !== null) return;
    if (data.period.status === 'SIGNED_OFF') {
      onFeedback({ tone: 'error', message: 'This period is frozen. Use the correction workflow before adding evidence.' }); return;
    }
    setPendingFiles(Array.from(files));
    setEvidencePanel(null);
  };

  const period = data?.period;
  // Base approval closes the collateral cycle. Collections remain actionable
  // until their own sign-off freezes the evidence and recorded decisions.
  const frozen = period?.status === 'SIGNED_OFF';

  // F-MK-076: the period on screen is the one to come back to.
  useEffect(() => {
    if (period?.id) rememberCollectionsPeriod(facilityId, period.id);
  }, [facilityId, period?.id]);

  // Keep the worked period visible in the rail without yanking the page around.
  useEffect(() => {
    activePeriodRef.current?.scrollIntoView({ block: 'nearest' });
  }, [period?.id]);
   const loadMatching = useCallback(async () => {
    if (!period) return;
    try { const value = await getMatching(facilityId, period.id); if (currentPeriodId.current === period.id) setMatching(value); }
    catch { setMatching(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, period?.id]);

  useEffect(() => { if (task === 'statement') loadMatching(); }, [task, loadMatching]);

  const openLines = useCallback(async (account: AccountRow) => {
    if (!period) return;
    setActionError('');
    setLinesFor(account);
    setLines(null);
    try {
      const result = await getBankLines(facilityId, period.id, account.id);
      setLines(result.lines);
      setLineClassifications(result.classifications || []);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Could not load the bank lines') });
      setLinesFor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, period?.id]);

  const refreshChecks = async () => {
    if (!period) return;
    const result = await act('refresh-checks', () => refreshCollectionsPeriod(facilityId, period.id), 'Checks refreshed');
    if (result) { setAutomation(result.message); setEvidenceChanged(result.outcome !== 'REFRESHED'); }
  };

  const reviewIssue = (item: ReconcilingItem) => {
    setTask('statement'); setOpenItem(item.id); setDisposition(item.disposition || '');
    setNotes(item.disposition_notes || ''); setRevisionReason(''); setAlsoSiblings(false);
  };
  const reviewLoan = async (identifier: string) => {
    if (!period) return;
    setLoanFor(identifier); setLoan(null); setTask('statement');
    try { const value = await getLoanDrillDown(facilityId, period.id, identifier); if (currentPeriodId.current === period.id) setLoan(value); }
    catch (error) { onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Could not open loan evidence') }); setLoanFor(null); }
  };
  const confirmAction = async () => {
    if (!period || !pendingAction || !actionConfirmed) return;
    const key = pendingAction;
    const result = await act(key, () => key === 'book' ? bookLenderCash(facilityId, period.id)
      : key === 'finalize' ? finalisePeriodWaterfall(facilityId, period.id, period.waterfallCheck.draftRunId, notes)
      : key === 'attest' ? attestPeriod(facilityId, period.id, notes) : countersignPeriod(facilityId, period.id, notes),
      key === 'book' ? 'Lender cash recorded through the governed posting workflow' : 'Review decision recorded');
    if (result) { setPendingAction(null); setActionConfirmed(false); setNotes(''); }
  };

  const num = (key: string) => {
    const raw = (form[key] ?? '').trim();
    if (!raw) return undefined;
    const parsed = Number(raw.replace(/[, $]/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  };

 const rail = period?.closeOutRail ?? [];
  const items = period?.statement?.items ?? [];
  const breaks = items.filter(i => i.item_class === 'BREAK' && !i.disposition);
  const statementRun = Boolean(period?.signals?.statementRun);

  if (loading) return <Shell><Note>Loading the collection periods…</Note></Shell>;
  if (!data) return <Shell><Note>No collections data for this facility.</Note></Shell>;

  return (
    <Shell>
      <ContextBar>
        <div>
          <span>Business date</span>
          <strong>{fmtDate(data.businessDate)}</strong>
          <small>{data.businessDateSource === 'MANUAL'
            ? 'Set by an administrator'
            : data.businessDateSource === 'EXTERNAL_FEED'
              ? 'From the official book'
              : 'Rolled by end of day'}</small>
        </div>
        <div>
          <span>Period</span>
          <strong>{period?.period_label ?? '—'}</strong>
          <small>{period?.stageLabel}{period?.version_no && period.version_no > 1
            ? ` · restated ×${period.version_no - 1}` : ''}</small>
        </div>
        {/* F-ACI-004's rule, in the header: a figure a control never produced is
            not a clean result. Until a statement exists there is nothing to
            report, and saying "nothing uncategorised" or "all dispositioned"
            before any work has happened is the most misleading thing this strip
            could say. */}
        <div>
          <span>Unexplained</span>
          <strong>{statementRun ? money(period?.statement?.unexplainedTotal ?? 0, ccy) : '—'}</strong>
          <small>{!statementRun ? 'No statement produced yet'
            : (period?.statement?.unexplainedTotal ?? 0) === 0
              ? 'Nothing uncategorised' : 'Must be zero to reconcile'}</small>
        </div>
        <div>
          <span>Open comparison rows</span>
          <strong>{statementRun ? breaks.length : '—'}</strong>
          <small>{!statementRun ? 'No statement produced yet'
            : breaks.length ? 'Grouped into issues in Statement and breaks' : 'Treatments recorded; inspect cash controls'}</small>
        </div>
        <div>
          <span>Deliverables</span>
          <strong>{period?.signals?.deliverablesReceived ?? 0} of {period?.signals?.deliverablesRequired ?? 0}</strong>
          <small>{period?.signals?.deliverablesLate
            ? `${period.signals.deliverablesLate} late` : 'None late'}</small>
        </div>
      </ContextBar>

      {!data.readiness.ready && (
        <Card style={{ borderColor: 'rgba(248,113,113,.4)', marginBottom: 14 }}>
          <CardHead><h3><AlertTriangle size={15} /> This facility cannot close a period yet</h3></CardHead>
          {data.readiness.blockers.map(b => (
            <Note key={b.key}><strong>{b.reason}.</strong> {b.remedy}</Note>
          ))}
        </Card>
      )}

      {data.unassignedEvidence?.total > 0 && (
        <Note style={{ marginBottom: 14 }}>
          <strong>{plural(data.unassignedEvidence.total, 'piece')} of evidence {data.unassignedEvidence.total === 1 ? 'belongs' : 'belong'} to no collection period.</strong>{' '}
          {data.unassignedEvidence.reason}
        </Note>
      )}

      {period && (
        <>
          <Layout>
            <TaskRail aria-label="Collection periods">
              <div className="rail-heading">
                <h2>Periods</h2>
                <p>Select a period to close it</p>
              </div>
              <div className="tasks">
                {(() => {
                  // F-S6-031: current and past periods newest first; months not yet started grouped
                  // under "Upcoming" at the bottom. The working period is selected by the server.
                  const upcoming = (chip: CollectionsPeriodChip) => isUpcomingPeriod(chip.periodStart, data.businessDate);
                  const byNewest = (a: CollectionsPeriodChip, b: CollectionsPeriodChip) => String(b.periodStart).localeCompare(String(a.periodStart));
                  const renderChip = (chip: CollectionsPeriodChip) => (
                  <TaskButton key={chip.id} type="button" disabled={periodChangeLocked} $active={chip.id === period?.id}
                    ref={chip.id === period?.id ? activePeriodRef : undefined}
                    aria-pressed={chip.id === period?.id} onClick={() => selectPeriod(chip.id)}>
                    <div className="category">
                      {shortDate(chip.periodStart)} – {shortDate(chip.periodEnd)}
                    </div>
                    <strong>{chip.periodLabel}</strong>
                    <div className="category" style={{ marginTop: 2 }}>det {shortDate(chip.determinationDate)} · pay {shortDate(chip.paymentDate)}</div>
                    <StageLine aria-label="Progress">
                      {progressOf(chip).map((state, index) => (
                        <li key={index} data-state={state === 'IDLE' ? undefined : state}
                          title={PROGRESS_LABELS[index]} />
                      ))}
                    </StageLine>
                    <span className="reason" style={{
                      color: chip.authorityStatus === 'SIGNED_OFF' ? '#7cddbe' : chip.authorityStatus === 'CLOSED' ? '#a8bacd' : undefined,
                    }}>
                      {finishedLine(chip)
                        ? <Check size={12} /> : <CircleDot size={12} />}
                      {nextActionOf(chip)}
                    </span>
                    {chip.deliverablesLate > 0 && !finishedLine(chip) && (
                      <span className="owner">{plural(chip.deliverablesLate, 'deliverable')} late</span>
                    )}
                  </TaskButton>
                );
                  const current = data.periods.filter(chip => !upcoming(chip)).sort(byNewest);
                  const later = data.periods.filter(upcoming).sort((a, b) => String(a.periodStart).localeCompare(String(b.periodStart)));
                  return <>
                    {current.map(renderChip)}
                    {later.length > 0 && <div className="group-heading">Upcoming<span>{later.length}</span></div>}
                    {later.map(renderChip)}
                  </>;
                })()}
                {!data.periods.length && <p className="empty">No periods on the calendar.</p>}
              </div>
            </TaskRail>

            <Work aria-label="Selected period">
              <Navigation aria-label="Period sections">
                {TASKS.map(section => (
                  <button key={section.key} type="button" aria-pressed={task === section.key}
                    onClick={() => setTask(section.key)}>{section.label}</button>
                ))}
                <span className="context">
                  <CalendarDays size={13} /> {period.period_label} · {period.status === 'SIGNED_OFF' ? 'Signed off' : period.status === 'CLOSED' ? 'Borrowing base approved' : period.stageLabel}
                </span>
              </Navigation>

              <CalendarStrip aria-label="Contract calendar for the selected period">
                {period.calendarStrip.map(stop => (
                  <li key={stop.key} data-state={stop.state}>
                    <span>{stop.label}</span>
                    <strong>{fmtDate(stop.date)}</strong>
                    <small>{stop.state === 'TODAY' ? 'Today'
                      : stop.daysAway === null ? ''
                      : stop.daysAway < 0 ? `${Math.abs(stop.daysAway)} ${Math.abs(stop.daysAway) === 1 ? 'day' : 'days'} ago`
                      : `in ${stop.daysAway} ${stop.daysAway === 1 ? 'day' : 'days'}`}</small>
                  </li>
                ))}
              </CalendarStrip>

              {!['close', 'deliverables'].includes(task) && <CollectionsGuidance data={data} busy={busy !== null} changed={evidenceChanged} onSection={setTask} onRefresh={() => void refreshChecks()} />}
              {automation && task !== 'statement' && <Note role="status" style={{ marginBottom: 16 }}>{automation}</Note>}

              {task === 'period' && (
                <Readable>
                  <CashStory period={period} currency={ccy} onSection={setTask} />
                  <CollectionsHandoff facilityId={facilityId} periodId={period.id} revision={data} />
                  <h3 style={{ marginTop: 20 }}>Period control checklist</h3>
                  <p>
                    Collections received between {fmtDate(period.period_start)} and {fmtDate(period.period_end)} are
                    determined on {fmtDate(period.determination_date)} and distributed on {fmtDate(period.payment_date)}.
                    {period.signed_off_at
                      ? ` Signed off on ${fmtDateTime(period.signed_off_at)} by ${period.signed_off_checker}, attested by ${period.signed_off_by}.`
                      : ''}
                  </p>
                  <Rail>
                    {rail.map(step => (
                      <li key={step.key} data-state={step.state}>
                        <Chip state={step.state} />
                        <span><strong>{step.label}</strong><small>{readableDetail(step.detail)}</small></span>
                      </li>
                    ))}
                  </Rail>

                  <h3 style={{ marginTop: 16 }}>Daily alerts</h3>
                  <Note>
                    Cash in an account the contract does not control, collections behind the tape's
                    run-rate, an unidentified deposit past its threshold, and a late deliverable.
                    Each is idempotent, so running this again raises nothing new.
                  </Note>
                  <Button disabled={busy !== null}
                    onClick={async () => {
                      const result = await act('alerts', () => evaluateAlerts(facilityId),
                        'Alerts evaluated');
                      if (result) setAlertRun(result);
                    }}>
                    <AlertTriangle size={14} /> Run the daily alerts
                  </Button>
                  {/* F-S5-070 — a runner that says "evaluated" and nothing else
                      leaves the operator asking what it found and where it went. */}
                  {alertRun && (
                    <Card style={{ marginTop: 12 }}>
                      <CardHead><h3>{alertRun.alertsRaised
                        ? `${plural(alertRun.alertsRaised, 'alert')} raised as of ${fmtDate(alertRun.businessDate)}`
                        : `Nothing new as of ${fmtDate(alertRun.businessDate)}`}</h3></CardHead>
                      <Note>
                        Alerts are raised into the facility's exception queue, so they are worked
                        on the <strong>Monitoring</strong> tab alongside every other exception and
                        count towards "Current exceptions" in the header. Running this again
                        raises nothing already open.
                      </Note>
                      {(alertRun.alerts || []).length > 0 && (
                        <Table>
                          <thead><tr><th>Alert</th><th>Detail</th></tr></thead>
                          <tbody>
                            {(alertRun.alerts || []).map((a: any, i: number) => (
                              <tr key={i}>
                                <td>{a.title || String(a.alertType || a.type || 'Alert')
                                  .replace(/_/g, ' ').toLowerCase()}</td>
                                <td><small>{a.description || a.detail || a.message || ''}</small></td>
                              </tr>))}
                          </tbody>
                        </Table>)}
                    </Card>
                  )}
                </Readable>
              )}

              {task === 'accounts' && (
                <Readable>
                  <CollectionsAccounts data={period.accountMap} currency={ccy} busy={busy !== null}
                    onOpen={account => void openLines(account)} onEvidence={() => setTask('deliverables')} />

                  {linesFor && (
                    <CollectionsDrawer title={`${linesFor.accountName || linesFor.accountKey} — bank lines`} width={1000}
                      onClose={() => { setLinesFor(null); setReclassify(null); }}>
                    <div style={{ overflowX: 'auto' }}>
                      {actionError && <Note role="alert" style={{ marginBottom: 14 }}>{actionError}</Note>}
                      <p style={{ color: INK.muted, fontSize: 12, marginBottom: 16 }}>
                        Bank value dates: {fmtDate(period.period_start)} to {fmtDate(period.payment_date)}.
                        {' '}This window can include cash assigned to another collection period, shown on each line.
                      </p>
                      {!lines ? <Note>Loading…</Note> : lines.length === 0 ? (
                        <Note>No lines on this account inside the period's window.</Note>
                      ) : (
                        <Table>
                          <thead><tr>
                            <th>Value date</th><th>Reference</th><th className="num">Amount</th>
                            <th>Collection period</th><th>Classification</th><th>Matched</th><th>Action</th>
                          </tr></thead>
                          <tbody>
                            {lines.map(line => (
                              <tr key={line.id}>
                                <td>{fmtDate(line.value_date)}<small>{line.lane}</small></td>
                                <td>{line.bank_transaction_id}
                                  <small>{line.reference_text}</small></td>
                                <td className="num">
                                  {line.direction === 'DEBIT' ? '(' : ''}
                                  {money(line.amount, line.currency || ccy)}
                                  {line.direction === 'DEBIT' ? ')' : ''}</td>
                                <td>{line.collection_period_id == null ? 'Unassigned'
                                  : data.periods.find(item => item.id === line.collection_period_id)?.periodLabel || 'Period unavailable'}</td>
                                <td><Chip state={line.classification} />
                                  <small>{line.classification_basis === 'MANUAL'
                                    ? `set by ${line.classified_by}` : 'by rule'}</small>
                                  {line.classification_reason &&
                                    <small>{line.classification_reason}</small>}
                                  {line.sweep_pair_transaction_id &&
                                    <small>paired with line #{line.sweep_pair_transaction_id}</small>}</td>
                                <td>{money(line.matched_amount, line.currency || ccy)}</td>
                                <td>
                                  <SmallButton onClick={() => setReclassify(
                                    reclassify?.id === line.id ? null
                                      : { id: line.id, to: line.classification, reason: '' })}>
                                    Reclassify
                                  </SmallButton>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}

                      {reclassify && (
                        <div style={{ marginTop: 12 }}>
                          <Note>
                            A person's classification is never overwritten by the automatic rules,
                            so the server records who decided and why, and refuses without a reason.
                          </Note>
                          <Field>
                            <FieldLabel htmlFor="rc">Classify as</FieldLabel>
                            <Select id="rc" value={reclassify.to}
                              onChange={e => setReclassify({ ...reclassify, to: e.target.value })}>
                              {lineClassifications.map(c => (
                                <option key={c} value={c}>
                                  {c.replace(/_/g, ' ').toLowerCase()}
                                </option>))}
                            </Select>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="rcr">Why</FieldLabel>
                            <TextArea id="rcr" value={reclassify.reason}
                              onChange={e => setReclassify({ ...reclassify, reason: e.target.value })} />
                          </Field>
                          <Button $variant="primary"
                            disabled={busy !== null || !reclassify.reason.trim()}
                            onClick={async () => {
                              const ok = await act('reclassify', () => classifyBankLine(
                                facilityId, reclassify.id, reclassify.to, reclassify.reason),
                                'Classification recorded');
                              if (ok) { setReclassify(null); if (linesFor) openLines(linesFor); }
                            }}>Record the classification</Button>
                        </div>
                      )}
                    </div>
                    </CollectionsDrawer>
                  )}
                </Readable>
              )}

              {task === 'deliverables' && (
                <Readable>
                  <CollectionsDeliverables key={`${facilityId}:${period.id}`} items={period.deliverables} accounts={period.accountMap.accounts}
                    periodLabel={period.period_label} currency={ccy} frozen={frozen} busy={busy !== null}
                    onAdd={item => {
                      setActionError('');
                      if (item?.kind === 'SERVICER_REPORT' || item?.kind === 'PAYING_AGENT_REPORT') {
                        setDocumentType(item.kind === 'PAYING_AGENT_REPORT' ? 'ABF_PAYING_AGENT_REPORT' : 'ABF_SERVICER_REPORT'); setEvidencePanel('pdf');
                      } else { setIntakeKind(item?.kind || 'ANY'); setDropAccount(period.accountMap.accounts.find(account => account.accountKey === item?.accountKey)?.id || ''); setEvidencePanel('files'); }
                    }}
                    onPdf={() => { setActionError(''); setDocumentType('ABF_SERVICER_REPORT'); setEvidencePanel('pdf'); }}
                    onManual={() => { setActionError(''); setEvidencePanel('servicer'); }}
                    onCollateral={() => navigate(`/portfolio-management/credit/abf/facilities/${facilityId}/collateral?collateralView=period&period=${period.id}`)}
                    onWaive={item => { setActionError(''); setWaiveReason(''); setWaiveId(item.id); }}
                    onWithdraw={file => { setActionError(''); setWithdrawReason(''); setWithdrawFile({ id: file.id, name: file.name }); }} />
                  {evidencePanel && <CollectionsDrawer title={evidencePanel === 'pdf' ? `Read a report for ${period.period_label}` : evidencePanel === 'servicer' ? `Record servicer figures for ${period.period_label}` : `Add ${intakeKind === 'REMITTANCE_DETAIL' ? 'payment detail' : 'bank evidence'} for ${period.period_label}`}
                    width={evidencePanel === 'servicer' ? 760 : 700} closeDisabled={busy !== null || documentBusy}
                    onClose={() => { if (busy === null && !documentBusy) setEvidencePanel(null); }}>
                    {actionError && <Note role="alert" style={{ marginBottom: 12 }}>{actionError}</Note>}
                    {evidencePanel === 'pdf' && <CollectionsDocumentIntake key={`${facilityId}:${period.id}:${documentType}`} facilityId={facilityId} facilityCode={String(data.facility.facility_code || '')} periodId={period.id} periodLabel={period.period_label}
                      initialDocumentType={documentType} onBusyChange={setDocumentBusy} locked={frozen} onImported={async () => { if (currentPeriodId.current === period.id) { await refreshChecks(); await load(); } }} />}
                    {evidencePanel === 'files' && <>
                      <Note style={{ marginBottom: 16 }}>Selected period: <strong>{period.period_label}</strong>. Review each file and its account before recording it. Existing evidence stays on record until explicitly withdrawn.</Note>
                  {intakeKind !== 'REMITTANCE_DETAIL' && <Field>
                    <FieldLabel htmlFor="drop-account">Default bank account — confirm each file before recording</FieldLabel>
                    <Select id="drop-account" value={dropAccount}
                      onChange={e => setDropAccount(e.target.value ? Number(e.target.value) : '')}>
                      <option value="">Choose a contract account…</option>
                      {period.accountMap.accounts.map(a => (
                        <option key={a.id} value={a.id}>{a.accountName || a.accountKey} — {a.accountType}{a.maskedAccountNumber ? ` · ${a.maskedAccountNumber}` : ''}</option>
                      ))}
                    </Select>
                  </Field>}
                  <Drop $over={dragOver}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}>
                    <Upload size={18} />
                    <span>Drop a bank statement (MT940 or CSV) or a remittance detail, or click to
                      choose one. Review the content and confirm its account in the file preview before it is recorded. A bank statement needs an account; payment detail does not. The server enforces period and duplicate controls.</span>
                    <input type="file" accept=".csv,.sta,.mt940,.mt942,.txt" multiple disabled={busy !== null || frozen} onChange={e => { void handleFiles(e.target.files); e.currentTarget.value = ''; }} />
                  </Drop>
                  {formats && (
                    <Formats>
                      <details>
                        <summary>Bank statement — CSV format help</summary>
                        <p><strong>Required columns:</strong> {(formats.bankCsvRequired || []).join(', ')}</p>
                        <p><strong>Optional:</strong> {(formats.bankCsvOptional || []).join(', ')}</p>
                        <p>{formats.bankCsvNotes}</p>
                      </details>
                      <details>
                        <summary>Bank statement — MT940 / MT942</summary>
                        <p>{formats.mt940}</p>
                      </details>
                      <details>
                        <summary>Remittance detail — CSV</summary>
                        <p><strong>Required columns:</strong> {(formats.remittanceCsvRequired || []).join(', ')}</p>
                        <p><strong>Optional:</strong> {(formats.remittanceCsvOptional || []).join(', ')}</p>
                        <p>{formats.remittanceCsvNotes}</p>
                      </details>
                      <details>
                        <summary>How a credit is classified</summary>
                        <p>{(formats.classifications || []).map((c: string) =>
                          c.replace(/_/g, ' ').toLowerCase()).join(' · ')}</p>
                      </details>
                    </Formats>
                  )}
                    </>}
                    {evidencePanel === 'servicer' && <>
                    <Card style={{ marginTop: 8 }}>
                      <Note>
                        The same governed record as the document lane: no capability on this screen
                        is gated on a PDF. The report date defaults to this period's determination
                        date, {fmtDate(period.determination_date)}.
                      </Note>
                      <Grid2>
                        <div>
                          {[['principal', 'Scheduled principal'], ['interest', 'Interest'],
                            ['prepayment', 'Prepayments'], ['recovery', 'Recoveries']].map(
                            ([key, label]) => (
                            <Field key={key}>
                              <FieldLabel htmlFor={`sr-${key}`}>{label}</FieldLabel>
                              <Input id={`sr-${key}`} inputMode="decimal" value={form[key] ?? ''}
                                onChange={e => setForm({ ...form, [key]: e.target.value })}
                                onBlur={e => setForm({ ...form,
                                  [key]: replaceShorthandOnBlur(e.target.value) })} />
                            </Field>))}
                        </div>
                        <div>
                          {[['feesNetted', 'Fees netted before remitting'],
                            ['advances', 'Servicer advances'],
                            ['repurchases', 'Repurchase proceeds']].map(([key, label]) => (
                            <Field key={key}>
                              <FieldLabel htmlFor={`sr-${key}`}>{label}</FieldLabel>
                              <Input id={`sr-${key}`} inputMode="decimal" value={form[key] ?? ''}
                                onChange={e => setForm({ ...form, [key]: e.target.value })}
                                onBlur={e => setForm({ ...form,
                                  [key]: replaceShorthandOnBlur(e.target.value) })} />
                            </Field>))}
                          <Field>
                            <FieldLabel htmlFor="sr-ref">Source reference</FieldLabel>
                            <Input id="sr-ref" value={form.srRef ?? ''}
                              onChange={e => setForm({ ...form, srRef: e.target.value })} />
                          </Field>
                        </div>
                      </Grid2>
                      <Button $variant="primary" disabled={busy !== null}
                        onClick={async () => {
                          const ok = await act('servicer-report', () => recordServicerReport(
                            facilityId, {
                              periodId: period.id, entryChannel: 'KEYED',
                              sourceReference: form.srRef || undefined,
                              currency: ccy,
                              principal: num('principal'), interest: num('interest'),
                              prepayment: num('prepayment'), recovery: num('recovery'),
                              feesNetted: num('feesNetted'), advances: num('advances'),
                              repurchases: num('repurchases'),
                            }), 'Servicer report recorded');
                          if (ok) { setForm({}); setEvidencePanel(null); }
                        }}>
                        <Keyboard size={14} /> Record the servicer report
                      </Button>
                    </Card>
                    </>}
                  </CollectionsDrawer>}
                  {withdrawFile !== null && (
                    <CollectionsDrawer title={`Withdraw ${withdrawFile.name}`} closeDisabled={busy !== null} onClose={() => setWithdrawFile(null)}>
                      {actionError && <Note role="alert">{actionError}</Note>}
                      <Note>
                        For a file dropped by mistake: the wrong facility, account or month. The file,
                        its lines and every match it made are recorded on Activity, the lines it paid
                        are put back, and the cash control has to be run again. Refused once a period
                        is signed off, a final waterfall used this cash, or lender cash was booked from it.
                      </Note>
                      <Field>
                        <FieldLabel htmlFor="withdraw-reason">
                          Why is this file being withdrawn?
                        </FieldLabel>
                        <TextArea id="withdraw-reason" value={withdrawReason}
                          onChange={e => setWithdrawReason(e.target.value)} />
                      </Field>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button $variant="primary"
                          disabled={busy !== null || withdrawReason.trim().length < 15}
                          onClick={async () => {
                            const ok = await act(`withdraw:${withdrawFile.id}`,
                              () => withdrawBankFile(facilityId, withdrawFile.id, withdrawReason),
                              `${withdrawFile.name} withdrawn — produce the statement and run the cash control again`);
                            if (ok) { setWithdrawFile(null); setWithdrawReason(''); }
                          }}>Withdraw the file</Button>
                        <Button onClick={() => { setWithdrawFile(null); setWithdrawReason(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </CollectionsDrawer>
                  )}
                  {waiveId !== null && (
                    <CollectionsDrawer title="Waive a required deliverable" closeDisabled={busy !== null} onClose={() => setWaiveId(null)}>
                      {actionError && <Note role="alert">{actionError}</Note>}
                      <Note>
                        The contract calendar expects this item. Waiving it records who decided and
                        why, and removes it from what sign-off requires — so the server asks for a
                        reason and refuses without one.
                      </Note>
                      <Field>
                        <FieldLabel htmlFor="waive-reason">
                          Why is this not expected for this period?
                        </FieldLabel>
                        <TextArea id="waive-reason" value={waiveReason}
                          onChange={e => setWaiveReason(e.target.value)} />
                      </Field>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button $variant="primary"
                          disabled={busy !== null || !waiveReason.trim()}
                          onClick={async () => {
                            const ok = await act(`waive:${waiveId}`,
                              () => waiveDeliverable(facilityId, waiveId, waiveReason),
                              'Deliverable waived');
                            if (ok) { setWaiveId(null); setWaiveReason(''); }
                          }}>Record the waiver</Button>
                        <Button onClick={() => { setWaiveId(null); setWaiveReason(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </CollectionsDrawer>
                  )}

                </Readable>
              )}

              {task === 'statement' && (
                <Readable>
                  <h2>Three-way statement</h2><Note style={{ margin: '10px 0' }}>Compare what the tape implies, what the servicer reports, and what the bank received by the cash cutoff. Matching late cash does not make it available for an earlier distribution.</Note>
                  {period.statement.run ? (
                    <Table>
                      <thead><tr><th>Column</th><th className="num">Amount</th><th>Source</th></tr></thead>
                      <tbody>
                        <tr>
                          <td>Tape-implied</td>
                          <td className="num">{money(period.statement.run.tape_total, ccy)}</td>
                          <td>Roll-forward against the prior released tape
                            <small>scheduled {money(period.statement.run.tape_scheduled_principal, ccy)} ·
                              prepayments {money(period.statement.run.tape_prepayments, ccy)} ·
                              recoveries {money(period.statement.run.tape_recoveries, ccy)} ·
                              interest {money(period.statement.run.tape_interest, ccy)}</small></td>
                        </tr>
                        <tr>
                          <td>Servicer-reported</td>
                          <td className="num">{money(period.statement.run.servicer_total, ccy)}</td>
                          <td>Servicer report
                            <small>its own loan detail sums to {money(period.statement.run.servicer_detail_total, ccy)}</small></td>
                        </tr>
                        <tr>
                          <td>Bank received</td>
                          <td className="num">{money(period.statement.run.bank_total, ccy)}</td>
                          <td>Matched credits on or before the determination date
                            {/* The fourth number the three-way statement never
                                stated: cash that arrived and nothing claims
                                (F-S5-051). It is a difference in its own right,
                                so it is named here and listed below. */}
                            {(() => {
                              const unapplied = items.find(
                                (i: ReconcilingItem) => i.direction === 'CASH_VS_DETAIL');
                              return unapplied ? (
                                <small data-tone="warning">
                                  {money(unapplied.amount, ccy)} more reached the accounts as
                                  collections and no remittance line claims it — listed below
                                </small>) : null;
                            })()}</td>
                        </tr>
                      </tbody>
                    </Table>
                  ) : (
                    <Note>No statement has been produced for this period yet.</Note>
                  )}
                  <div style={{ margin: '12px 0' }}>
                    <Button $variant="primary" disabled={busy !== null}
                      onClick={() => void refreshChecks()}>
                      <RefreshCw size={14} /> {busy === 'refresh-checks' ? 'Refreshing statement and cash checks…' : 'Refresh statement and cash checks'}
                    </Button>
                    <ActionLocalFeedback feedback={actionError
                      ? { tone: 'error', message: actionError }
                      : automation ? { tone: evidenceChanged ? 'warning' : 'info', message: automation } : null} />
                  </div>

                  <details><summary style={{ cursor: 'pointer', color: INK.accentSoft }}>Bank matching and remittance evidence</summary>
                  <h3>Cash allocated to the remittance lines</h3>
                  <Note>
                    The bank column can only be compared once the cash that paid the remittance
                    lines is allocated to them. This runs on its own when the statement is produced;
                    only credits on the collection account are eligible, because a lockbox credit is
                    the same money before the sweep.
                  </Note>
                  {matching && (
                    <>
                      <Strip style={{ marginTop: 10 }}>
                        <div>
                          <span>Remittance lines</span>
                          <strong>{matching.remittanceLines}</strong>
                          <small>{money(matching.remittanceTotal, ccy)}</small>
                        </div>
                        <div>
                          <span>Allocated</span>
                          <strong>{money(matching.remittanceMatched, ccy)}</strong>
                          <small>{matching.linesFullyMatched} fully · {matching.linesPartiallyMatched} partly</small>
                        </div>
                        <div>
                          <span>Not allocated</span>
                          <strong>{matching.linesUnmatched}</strong>
                          <small>{pluralWord(matching.linesUnmatched, 'line')} with no cash against them</small>
                        </div>
                      </Strip>
                      <Table>
                        <thead><tr>
                          <th>Credit</th><th>Account</th><th className="num">Amount</th>
                          <th className="num">Allocated</th><th>State</th>
                        </tr></thead>
                        <tbody>
                          {(matching.credits || []).map((credit: any) => {
                            // Only a servicer remittance on the collection account is
                            // allocated. A distribution or account interest is listed for
                            // completeness; "unmatched" would read as a problem it is not.
                            const eligible = String(credit.classification) === 'SERVICER_REMITTANCE'
                              && String(credit.account_type || 'COLLECTION') === 'COLLECTION';
                            const accountRow = period.accountMap.accounts.find((a: AccountRow) => a.accountKey === credit.account_key);
                            return (
                            <tr key={credit.id}>
                              <td>{credit.bank_transaction_id}<small>{fmtDate(credit.value_date)}</small></td>
                              <td title={credit.account_key}>{accountRow?.accountName || credit.account_key}
                                <small>{String(credit.classification).replace(/_/g, ' ').toLowerCase()}</small></td>
                              <td className="num">{money(credit.amount, ccy)}</td>
                              <td className="num">{eligible ? money(credit.matched_amount, ccy) : <span style={{ color: INK.faint }}>—</span>}</td>
                              <td>{eligible ? <Chip state={credit.status} /> : <StatusChip tone="neutral" label="Not a remittance" />}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      {(matching.unmatchedLines || []).length > 0 && (
                        <Note>
                          {plural(matching.linesUnmatched, 'remittance line')} {matching.linesUnmatched === 1 ? 'has' : 'have'} no cash against them —
                          largest: {(matching.unmatchedLines || []).slice(0, 3).map((l: any) =>
                            `${l.asset_identifier} ${money(l.amount, ccy)}`).join(', ')}.
                          The statement explains the shortfall; it does not need every line matched.
                        </Note>
                      )}
                      <Button disabled={busy !== null}
                        onClick={() => void refreshChecks()}>Retry matching and checks</Button>
                    </>
                  )}

                  </details>
                  <CasesWorkbench items={items} currency={ccy} busy={busy !== null} onInspect={reviewIssue} onLoan={id => void reviewLoan(id)} />
                  <details style={{ marginTop: 18 }}><summary style={{ cursor: 'pointer', color: INK.accentSoft }}>Comparison rows and revision history ({items.length})</summary>
                  <h3>Comparison-row audit</h3>
                  <Table>
                    <thead><tr>
                      <th>Difference</th><th>Compared</th>
                      <th className="num">Amount</th><th>State</th>
                    </tr></thead>
                    <tbody>
                      {items.map((item: ReconcilingItem) => (
                        <tr key={item.id}
                          data-tone={item.item_class === 'BREAK' ? 'break'
                            : item.item_class === 'TIMING' ? 'timing' : undefined}>
                          <td>
                            <span className="tag">{item.item_class}</span>
                            {item.category_title ?? sentenceCase(item.category)}
                            {isWaterfallNode(item) && item.loans?.[0] && (
                              <span className="node"> · {item.loans[0].asset_identifier}</span>)}
                            <small>{item.detection_basis}</small>
                            {item.expected_evidence && <small>Clears when {item.expected_evidence}</small>}
                            {!isWaterfallNode(item) && item.loans?.length > 0 && (
                              <small>{item.loans.map(l => (
                                <SmallButton key={l.asset_identifier} onClick={async () => {
                                  if (loanFor === l.asset_identifier) {
                                    setLoanFor(null); setLoan(null); return;
                                  }
                                  setLoanFor(l.asset_identifier); setLoan(null);
                                  try {
                                    setLoan(await getLoanDrillDown(
                                      facilityId, period.id, l.asset_identifier));
                                  } catch (error) {
                                    onFeedback({ tone: 'error', message: apiErrorMessage(error,
                                      `Could not open ${l.asset_identifier}`) });
                                    setLoanFor(null);
                                  }
                                }}>{l.asset_identifier}</SmallButton>))}</small>)}
                            {item.correlation_note && (
                              <small data-tone="hint">{
                                items.some(o => o.correlation_key === item.correlation_key
                                  && !o.disposition && o.status === 'OPEN')
                                  ? item.correlation_note
                                  : `One of ${items.filter(o =>
                                      o.correlation_key === item.correlation_key).length} lines `
                                    + 'showing the same money from different sides.'
                              }</small>)}</td>
                          <td>{item.direction.replace(/_/g, ' ').toLowerCase()}</td>
                          <td className="num">{money(Math.abs(item.amount), item.currency || ccy)}</td>
                          <td>
                            <Chip state={item.status} />
                            {item.disposition && <small>
                              {dispositions.find(d => d.code === item.disposition)?.label
                                || sentenceCase(item.disposition)}
                              {item.dispositioned_by ? ` · ${item.dispositioned_by}` : ''}
                              {item.dispositioned_at ? ` · ${shortWhen(item.dispositioned_at)}` : ''}</small>}
                            {item.item_class === 'BREAK' && !item.disposition && (
                              <SmallButton onClick={() => {
                                setOpenItem(item.id === openItem ? null : item.id);
                                setDisposition(''); setNotes(''); setRevisionReason('');
                              }}>Disposition</SmallButton>)}
                            {item.disposition && !period.signals?.signedOff && (
                              <SmallButton onClick={() => {
                                setOpenItem(item.id === openItem ? null : item.id);
                                setDisposition(item.disposition || '');
                                setNotes(item.disposition_notes || ''); setRevisionReason('');
                              }}>Revise</SmallButton>)}
                            {item.revisions?.length > 0 && (
                              <small>Revised {plural(item.revisions.length, 'time')} — was
                                {' '}{item.revisions[0].disposition_label
                                  || item.revisions[0].disposition} by
                                {' '}{item.revisions[0].dispositioned_by}
                                {' '}({item.revisions[0].revision_reason})</small>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </details>



                  {loanFor && (
                    <CollectionsDrawer key={loanFor} title={`Loan ${loanFor} — evidence and history`} onClose={() => { setLoanFor(null); setLoan(null); }}>
                    <Card style={{ marginTop: 12 }}>
                      <CardHead>
                        <h3>{loanFor}</h3>
                        <SmallButton onClick={() => { setLoanFor(null); setLoan(null); }}>
                          Close
                        </SmallButton>
                      </CardHead>
                      {!loan ? <Muted>Reading everything on record for this loan…</Muted> : (
                        <>
                          <Note data-tone={loan.eligibilityImpact?.state === 'INELIGIBLE'
                            ? 'warning' : undefined}>
                            {loan.eligibilityImpact?.note}
                            {loan.eligibilityImpact?.currentBalance != null
                              && ` Balance ${money(loan.eligibilityImpact.currentBalance, ccy)}.`}
                            {loan.eligibilityImpact?.ineligibilityReasons && ` Reasons: ${
                              (Array.isArray(loan.eligibilityImpact.ineligibilityReasons)
                                ? loan.eligibilityImpact.ineligibilityReasons
                                : [loan.eligibilityImpact.ineligibilityReasons])
                                .map((r: any) => String(r).replace(/_/g, ' ').toLowerCase())
                                .join(', ')}.`}
                          </Note>

                          <h4>Roll-forward, last four released tapes</h4>
                          <Table>
                            <thead><tr>
                              <th>As of</th><th>Period</th><th className="num">Balance</th>
                              <th className="num">Prepaid</th><th className="num">Recovered</th>
                              <th className="num">Charged off</th><th>Status</th>
                            </tr></thead>
                            <tbody>
                              {loan.rollForward.map((r: any) => (
                                <tr key={r.tape_ingest_id}>
                                  <td>{shortDate(r.as_of_date)}<small>tape #{r.tape_ingest_id}</small></td>
                                  <td>{r.period_label || '—'}</td>
                                  <td className="num">{money(r.current_balance, ccy)}</td>
                                  <td className="num">{money(r.prepayment_amount, ccy)}</td>
                                  <td className="num">{money(r.recovered_amount, ccy)}</td>
                                  <td className="num">{money(r.chargeoff_amount, ccy)}</td>
                                  <td>{r.asset_status}
                                    <small>{r.eligible ? 'eligible' : 'ineligible'}
                                      {r.days_past_due != null ? ` · ${r.days_past_due} dpd` : ''}</small></td>
                                </tr>
                              ))}
                              {loan.rollForward.length === 0 && (
                                <tr><td colSpan={7}><Muted>
                                  This loan is on no released tape for this facility.
                                </Muted></td></tr>)}
                            </tbody>
                          </Table>

                          <h4>Remittance lines</h4>
                          <Table>
                            <thead><tr>
                              <th>Reference</th><th>Date</th><th>Type</th>
                              <th className="num">Amount</th><th className="num">Matched</th><th>State</th>
                            </tr></thead>
                            <tbody>
                              {loan.remittanceLines.map((r: any) => (
                                <tr key={r.id}>
                                  <td>{r.source_reference}<small>{r.period_label || '—'}</small></td>
                                  <td>{shortDate(r.remittance_date)}</td>
                                  <td>{String(r.remittance_type || '').replace(/_/g, ' ').toLowerCase()}</td>
                                  <td className="num">{money(r.amount, ccy)}</td>
                                  <td className="num">{money(r.matched_amount, ccy)}</td>
                                  <td><Chip state={r.status} /></td>
                                </tr>
                              ))}
                              {loan.remittanceLines.length === 0 && (
                                <tr><td colSpan={6}><Muted>
                                  No remittance line has ever been reported for this loan. That
                                  absence is what the break is about.
                                </Muted></td></tr>)}
                            </tbody>
                          </Table>

                          <h4>Cash matched to those lines</h4>
                          <Table>
                            <thead><tr>
                              <th>Credit</th><th>Account</th><th>Value date</th>
                              <th className="num">Credit</th><th className="num">Matched</th><th>How</th>
                            </tr></thead>
                            <tbody>
                              {loan.matchedCash.map((m: any) => (
                                <tr key={m.id}>
                                  <td>{m.bank_transaction_id}</td>
                                  <td>{m.account_key}
                                    <small>{String(m.classification || '')
                                      .replace(/_/g, ' ').toLowerCase()}</small></td>
                                  <td>{shortDate(m.value_date)}</td>
                                  <td className="num">{money(m.credit_amount, ccy)}</td>
                                  <td className="num">{money(m.matched_amount, ccy)}</td>
                                  <td>{String(m.match_method || '').replace(/_/g, ' ').toLowerCase()}
                                    {m.matched_by ? <small>by {m.matched_by}</small> : null}</td>
                                </tr>
                              ))}
                              {loan.matchedCash.length === 0 && (
                                <tr><td colSpan={6}><Muted>
                                  No bank cash has been matched to this loan.
                                </Muted></td></tr>)}
                            </tbody>
                          </Table>

                          <h4>Every difference this loan has appeared in</h4>
                          <Table>
                            <thead><tr>
                              <th>Period</th><th>Class</th><th>Category</th>
                              <th className="num">Amount</th><th>State</th>
                            </tr></thead>
                            <tbody>
                              {loan.history.map((h: any) => (
                                <tr key={h.id}>
                                  <td>{h.period_label}</td>
                                  <td>{h.item_class}</td>
                                  <td>{String(h.category || '').replace(/_/g, ' ').toLowerCase()}
                                    <small>{String(h.direction || '')
                                      .replace(/_/g, ' ').toLowerCase()}</small></td>
                                  <td className="num">{money(h.amount, ccy)}</td>
                                  <td><Chip state={h.status} />
                                    {h.disposition && <small>{h.disposition
                                      .replace(/_/g, ' ').toLowerCase()}
                                      {h.dispositioned_by ? ` by ${h.dispositioned_by}` : ''}</small>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </Card></CollectionsDrawer>
                  )}
                  {openItem !== null && (() => {
                    const subject = items.find(i => i.id === openItem);
                    // A correlation is a hint, so the siblings are offered and
                    // never settled behind the operator's back (F-S5-044).
                    const siblings = subject?.correlation_key
                      ? items.filter(i => i.id !== openItem && !i.disposition
                          && i.correlation_key === subject.correlation_key)
                      : [];
                    return (
                    <CollectionsDrawer key={openItem} title="Review the issue and its evidence" onClose={() => setOpenItem(null)}>
                    {subject && <CaseEvidence subject={subject} items={items} currency={ccy} period={period} />}
                    {subject && !frozen && (subject.item_class === 'BREAK' || subject.disposition) ? <Card style={{ marginTop: 12 }}>
                      <CardHead><h3>{subject.disposition ? 'Revise the recorded treatment' : 'Record what happened'}</h3></CardHead>
                      <Field>
                        <FieldLabel htmlFor="disp">Disposition</FieldLabel>
                        <Select id="disp" value={disposition} onChange={e => setDisposition(e.target.value)}>
                          <option value="">Choose…</option>
                          {dispositions.map(d => (
                            <option key={d.code} value={d.code}>{d.label}</option>))}
                        </Select>
                        {disposition && (
                          <small>{dispositions.find(d => d.code === disposition)?.description}</small>)}
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="disp-notes">What was found, and how it was evidenced</FieldLabel>
                        <TextArea id="disp-notes" value={notes} onChange={e => setNotes(e.target.value)} />
                      </Field>
                      {subject?.disposition && (
                        <Field>
                          <FieldLabel htmlFor="rev-reason">Why this is being changed</FieldLabel>
                          <TextArea id="rev-reason" value={revisionReason}
                            onChange={e => setRevisionReason(e.target.value)} />
                          <small>Recorded as {subject.disposition.replace(/_/g, ' ').toLowerCase()}
                            {' '}by {subject.dispositioned_by}. What it says now is kept.</small>
                        </Field>
                      )}
                      {siblings.length > 0 && (
                        <Field>
                          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <input type="checkbox" checked={alsoSiblings}
                              onChange={e => setAlsoSiblings(e.target.checked)} />
                            <span>Apply this to the {plural(siblings.length, 'line')} very likely showing
                              the same money: {siblings.map(s =>
                                `${s.direction.replace(/_/g, ' ').toLowerCase()} ${money(s.amount, s.currency || ccy)}`
                              ).join('; ')}. Leave it unticked to read each one yourself.</span>
                          </label>
                        </Field>
                      )}
                      <Button $variant="primary"
                        disabled={!disposition || !notes.trim() || busy !== null
                          || (!!subject?.disposition && !revisionReason.trim())}
                        onClick={async () => {
                          const ok = await act('disposition', () => dispositionCase(facilityId, openItem,
                            { disposition, notes, applyRelated: alsoSiblings, revisionReason: revisionReason || undefined }),
                            'Issue treatment recorded; cash evidence remains independently controlled');
                          if (ok) {
                            setOpenItem(null); setDisposition(''); setNotes('');
                            setAlsoSiblings(false); setRevisionReason('');
                          }
                        }}>{subject?.disposition
                          ? 'Record the revision' : 'Record the disposition'}</Button>
                    </Card> : <Note>This issue is read-only here. Timing, cleared and frozen records remain evidence; corrections use their governed workflow.</Note>}
                    </CollectionsDrawer>
                  ); })()}

                  {period.statement.carriedIn?.length > 0 && (
                    <>
                      <h3 style={{ marginTop: 16 }}>Carried into this period</h3>
                      <Table>
                        <thead><tr><th>From</th><th>Category</th><th className="num">Amount</th></tr></thead>
                        <tbody>
                          {period.statement.carriedIn.map((item: any) => (
                            <tr key={item.id}>
                              <td>{item.raised_in_period}</td>
                              <td>{item.category.replace(/_/g, ' ').toLowerCase()}
                                <small>{item.expected_evidence}</small></td>
                              <td className="num">{money(item.amount, ccy)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Readable>
              )}

              {task === 'close' && (
                <Readable>
                  <CollectionsCloseout key={`${facilityId}:${period.id}`} period={period} currency={ccy} nodes={nodes} initialStep={params.get('review')}
                    readiness={readiness} busy={busy !== null} username={user?.username} notes={notes} onNotes={setNotes}
                    actionError={actionError} onClearError={() => setActionError('')}
                    onEvidence={() => setTask('deliverables')} onIssues={() => setTask('statement')} onIssue={reviewIssue}
                    onRefresh={() => void refreshChecks()}
                    onRunCash={() => void act('cash-control', () => runCashControl(facilityId, period.id), 'Cash verification updated')}
                    onRunDistribution={() => void act('waterfall-run', () => runPeriodWaterfall(facilityId, period.id), 'Distribution calculated for review')}
                    onDecision={decision => { setActionError(''); setActionConfirmed(false); setPendingAction(decision); }}
                    onRestate={async () => { const result = await act('restate', () => restatePeriod(facilityId, period.id, notes.trim()), 'New period version opened; the signed version is preserved'); if (result) setNotes(''); }}
                    projections={<>
                  {/* WS7 (row 3.2): the pre-receipt view — what the tape says we should collect, and the waterfall on it.
                      A signed-off period keeps the projection it has; it is not offered one. */}
                  {(!frozen || period.expectedCollections?.projected) && <>
                  <h3 style={{ marginTop: 16 }}>Projected, before the servicer reports</h3>
                  <Note data-tone="hint">
                    {period.expectedCollections?.projected
                      ? `Expected ${money(period.expectedCollections.total, ccy)} from ${period.expectedCollections.loans} loans on released tape ${period.expectedCollections.tape_ingest_id}: interest ${money(period.expectedCollections.interest, ccy)}, scheduled principal ${money(period.expectedCollections.principal, ccy)}, prepayments ${money(period.expectedCollections.prepayments, ccy)}, recoveries ${money(period.expectedCollections.recoveries, ccy)} (defaults ${money(period.expectedCollections.defaults, ccy)}${period.expectedCollections.scenario_code ? `, scenario ${period.expectedCollections.scenario_code}` : ', no active scenario: zero CPR/CDR'}). PROJECTED, nothing booked.`
                      : (period.expectedCollections?.note || 'Not projected yet. Projection derives scheduled interest and principal per loan from the latest released tape (payment frequency, amortization type, remaining term) and prepayments, defaults and recoveries from the active scenario.')}
                  </Note>
                  {!frozen && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0 10px' }}>
                    <SmallButton disabled={busy !== null} onClick={() => act('project-collections', () => projectExpectedCollections(facilityId, period.id), 'Expected collections projected from the released tape')}>
                      {period.expectedCollections?.projected ? 'Re-project collections' : 'Project collections'}
                    </SmallButton>
                    {period.expectedCollections?.projected && (
                      <SmallButton disabled={busy !== null} onClick={() => act('project-waterfall', () => projectPeriodWaterfall(facilityId, period.id), 'Projected waterfall run recorded (PROJECTED: books nothing)')}>
                        {period.waterfallCheck?.projectedRunId ? `Re-run projected waterfall (run #${period.waterfallCheck.projectedRunId})` : 'Project the waterfall'}
                      </SmallButton>)}
                    {period.waterfallCheck?.projectedSuperseded && <small style={{ alignSelf: 'center' }}>The projection is superseded by the run on matched cash; it stays as the Projected column.</small>}
                  </div>
                  )}
                  </>}

                    </>}
                    agentEditor={<>
                    <Card style={{ marginTop: 8 }}>
                      <Note>
                        {/* F-S5-059 — an evidence panel names the period it writes
                            to, at the action, not as a date inside a sentence. */}
                        <strong>Recording a distribution statement for {period.period_label}</strong>
                        {period.payment_date ? `, payment date ${fmtDate(period.payment_date)}` : ''}.
                        {' '}The agent's statement is the authority for how the distribution splits
                        between principal and interest. Keyed or extracted, it is one record.
                      </Note>
                      <Grid2>
                        <div>
                          <Field>
                            <FieldLabel htmlFor="pa-agent">Paying agent</FieldLabel>
                            <Input id="pa-agent" value={form.paAgent ?? ''}
                              onChange={e => setForm({ ...form, paAgent: e.target.value })} />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="pa-available">Available collections</FieldLabel>
                            <Input id="pa-available" inputMode="decimal" value={form.paAvailable ?? ''}
                              onChange={e => setForm({ ...form, paAvailable: e.target.value })}
                              onBlur={e => setForm({ ...form,
                                paAvailable: replaceShorthandOnBlur(e.target.value) })} />
                            <small>The top of the waterfall. The priority of payments below foots
                              to this, including the lines that are not ours.</small>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="pa-total">Distributed to the lender</FieldLabel>
                            <Input id="pa-total" inputMode="decimal" value={form.paTotal ?? ''}
                              onChange={e => setForm({ ...form, paTotal: e.target.value })}
                              onBlur={e => setForm({ ...form,
                                paTotal: replaceShorthandOnBlur(e.target.value) })} />
                            <small>What reached us — principal plus interest, and the two
                              have to agree.</small>
                          </Field>
                        </div>
                        <div>
                          <Field>
                            <FieldLabel htmlFor="pa-principal">Paid to the lender — principal</FieldLabel>
                            <Input id="pa-principal" inputMode="decimal" value={form.paPrincipal ?? ''}
                              onChange={e => setForm({ ...form, paPrincipal: e.target.value })}
                              onBlur={e => setForm({ ...form,
                                paPrincipal: replaceShorthandOnBlur(e.target.value) })} />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="pa-interest">Paid to the lender — interest</FieldLabel>
                            <Input id="pa-interest" inputMode="decimal" value={form.paInterest ?? ''}
                              onChange={e => setForm({ ...form, paInterest: e.target.value })}
                              onBlur={e => setForm({ ...form,
                                paInterest: replaceShorthandOnBlur(e.target.value) })} />
                          </Field>
                        </div>
                      </Grid2>
                      <Note>
                        Node lines — one per priority of payments line, as the statement shows them.
                        Without these the total can agree while the allocation is wrong, which is
                        the whole reason this check exists.
                      </Note>
                      {nodes.length === 0 && (
                        <Note>This facility's active version has no waterfall nodes, so there is
                          nothing to key line by line. The distribution total is still checked.</Note>)}
                      <Grid2>
                        {nodes.map(node => (
                          <Field key={node.id}>
                            <FieldLabel htmlFor={`pa-node-${node.id}`}>
                              {node.paying_agent_label || node.node_name}
                            </FieldLabel>
                            <Input id={`pa-node-${node.id}`} inputMode="decimal"
                              value={form[`node:${node.node_name}`] ?? ''}
                              onChange={e => setForm({ ...form, [`node:${node.node_name}`]: e.target.value })}
                              onBlur={e => setForm({ ...form,
                                [`node:${node.node_name}`]: replaceShorthandOnBlur(e.target.value) })} />
                            <small>
                              {node.lender_facing === true ? 'Paid to us. '
                                : node.lender_facing === false ? 'Not ours — checked, never booked. '
                                : 'Nobody has classified this node; it is read as ours. '}
                              {node.paying_agent_label
                                ? `The agent calls our "${node.node_name}" this.`
                                : 'No agent term recorded yet — key what the statement calls it '
                                  + 'below and the mapping is kept.'}
                              {node.source_clause ? ` ${node.source_clause}` : ''}
                            </small>
                            {!node.paying_agent_label && (
                              <Input placeholder="What the statement calls this line"
                                value={form[`label:${node.id}`] ?? ''}
                                onChange={e => setForm({ ...form, [`label:${node.id}`]: e.target.value })} />)}
                          </Field>))}
                      </Grid2>
                      <Button $variant="primary" disabled={busy !== null}
                        onClick={async () => {
                          // A label keyed beside a node is the agent's term for it,
                          // and confirming it once is what POST /waterfall-nodes/map
                          // is for. Do it before the statement, so the lines the
                          // report carries are the agent's own words.
                          for (const node of nodes.filter(n => !n.paying_agent_label)) {
                            const label = (form[`label:${node.id}`] || '').trim();
                            if (!label) continue;
                            try {
                              await mapWaterfallNode(facilityId,
                                { nodeName: node.node_name, payingAgentLabel: label });
                            } catch (error) {
                              onFeedback({ tone: 'error', message: apiErrorMessage(error,
                                `Could not record what the agent calls ${node.node_name}`) });
                            }
                          }
                          const nodeLines = nodes
                            .filter(n => (form[`node:${n.node_name}`] || '').trim())
                            .map(n => ({
                              agentLabel: (form[`label:${n.id}`] || '').trim()
                                || n.paying_agent_label || n.node_name,
                              paymentType: n.payment_type,
                              amount: Number(form[`node:${n.node_name}`].replace(/[, $]/g, '')),
                              citation: form.paRef || null,
                            }));
                          const ok = await act('paying-agent', () => recordPayingAgentReport(
                            facilityId, {
                              periodId: period.id, entryChannel: 'KEYED',
                              payingAgentName: form.paAgent || undefined,
                              currency: ccy,
                              availableCollections: num('paAvailable'),
                              distributionTotal: num('paTotal'),
                              lenderPrincipal: num('paPrincipal'),
                              lenderInterest: num('paInterest'),
                              lines: nodeLines,
                            }), "Paying agent's statement recorded");
                          if (ok) setForm({});
                        }}>
                        <Keyboard size={14} /> Record the statement
                      </Button>
                    </Card>
                    </>}
                    receiptDetails={<>
                  {period.lenderCash?.alreadyBooked && period.lenderCash?.receipt
                    && period.lenderCash?.booking?.status === 'BOOKED' && (() => {
                    // F-LR-058: the booking stays readable after the click: the split, the bank credit it ties
                    // to, the trades it posted and what it did to the drawn balance.
                    const r = period.lenderCash.receipt;
                    const ties = r.bankAmount != null && Number(r.bankAmount) === Number(r.total);
                    return (
                      <Table style={{ marginTop: 8 }}>
                        <tbody>
                          <tr><td>Principal</td><td className="num">{money(r.principal, ccy)}</td>
                            <td>{r.principalTradeId ? `trade #${r.principalTradeId}` : ''}</td></tr>
                          <tr><td>Interest</td><td className="num">{money(r.interest, ccy)}</td>
                            <td>{r.interestTradeId ? `trade #${r.interestTradeId}` : ''}</td></tr>
                          <tr><td><strong>Lender cash</strong></td><td className="num"><strong>{money(r.total, ccy)}</strong></td>
                            <td>trade {fmtDate(r.tradeDate)} · settles {fmtDate(r.settleDate)}</td></tr>
                          {r.bankAmount != null && (
                            <tr><td>Payment-account credit</td><td className="num">{money(r.bankAmount, ccy)}</td>
                              <td>{r.bankReference} · {fmtDate(r.bankValueDate)} · {ties ? 'ties to the cent' : 'differs'}</td></tr>)}
                          {r.drawnAfter != null && (
                            <tr><td>Drawn</td><td className="num">{money(r.drawnAfter, ccy)}</td>
                              <td>was {money(r.drawnBefore, ccy)}</td></tr>)}
                          {/* F-MK-053: every trade the booking posted, by fund. */}
                          {(r.trades || []).map((t: any) => (
                            <tr key={t.trade_id}><td>{t.portfolio_code || `portfolio ${t.portfolio_id}`} · {String(t.kind || '').toLowerCase()}</td>
                              <td className="num">{money(t.amount, ccy)}</td>
                              <td>trade #{t.trade_id} · {String(t.status || '').toLowerCase()}</td></tr>))}
                        </tbody>
                      </Table>
                    );
                  })()}
                  {/* F-MK-053: before booking, what each fund receives and whether the cash is there. */}
                  {period.lenderCash?.bookable && (
                    <Table style={{ marginTop: 8 }}>
                      <thead><tr><th>Fund</th><th className="num">Share</th><th className="num">Principal</th>
                        <th className="num">Interest</th><th className="num">Lender cash</th></tr></thead>
                      <tbody>
                        {(period.lenderCash.legs || []).map((leg: any) => (
                          <tr key={leg.portfolioId}><td>{leg.portfolioCode || leg.portfolioName}</td>
                            <td className="num">{Number(leg.sharePct).toFixed(2)}%</td>
                            <td className="num">{money(leg.principal, ccy)}</td>
                            <td className="num">{money(leg.interest, ccy)}</td>
                            <td className="num">{money(leg.total, ccy)}</td></tr>))}
                        <tr><td><strong>Total</strong></td><td />
                          <td className="num"><strong>{money(period.lenderCash.bookedPrincipal, ccy)}</strong></td>
                          <td className="num"><strong>{money(period.lenderCash.bookedInterest, ccy)}</strong></td>
                          <td className="num"><strong>{money(Number(period.lenderCash.bookedPrincipal) + Number(period.lenderCash.bookedInterest), ccy)}</strong></td></tr>
                        {period.lenderCash.bankCreditAmount != null && (
                          <tr><td>Payment-account credit</td><td /><td /><td />
                            <td className="num">{money(period.lenderCash.bankCreditAmount, ccy)}
                              <small> {fmtDate(period.lenderCash.bankValueDate)} · {Number(period.lenderCash.statementVersusBank) === 0 ? 'ties to the cent' : `differs by ${money(period.lenderCash.statementVersusBank, ccy)}`}</small></td></tr>)}
                      </tbody>
                    </Table>
                  )}
                    </>}
                    reversal={<>
                  {period.lenderCash?.alreadyBooked
                    && period.lenderCash?.booking?.status === 'BOOKED'
                    && period.status !== 'SIGNED_OFF' && (
                    <div style={{ marginTop: 16 }}>
                      <Note>Reverse the active receipt with a reason. The original booking stays in the record alongside its compensating entries.</Note>
                      <Field>
                        <FieldLabel htmlFor="rev-reason">Why</FieldLabel>
                        <TextArea id="rev-reason" value={form.revReason ?? ''}
                          onChange={e => setForm({ ...form, revReason: e.target.value })} />
                      </Field>
                      <Button $variant="danger"
                        disabled={busy !== null || !(form.revReason ?? '').trim()}
                        onClick={async () => {
                          const ok = await act('reverse', () => reverseLenderCash(
                            facilityId, period.lenderCash.booking.id, form.revReason),
                            'Booking reversed');
                          if (ok) setForm({ ...form, revReason: '' });
                        }}>Reverse the booking</Button>
                    </div>
                  )}

                    </>}
                  />
                </Readable>
              )}
              {pendingFiles.length > 0 && <CollectionsFileReview key={`${facilityId}:${period.id}`} files={pendingFiles} accounts={period.accountMap.accounts} initialAccount={String(dropAccount || '')} periodLabel={period.period_label} onClose={() => setPendingFiles([])}
                onRecord={(file, content, account) => act(`file:${file.name}`, () => dropEvidence(facilityId, { facilityAccountId: account, periodId: period.id, sourceFilename: file.name, currency: ccy, content }), `${file.name} recorded`)} />}
              {pendingAction && <CollectionsDrawer title={pendingAction === 'book' ? 'Confirm the official lender receipt' : pendingAction === 'finalize' ? 'Approve the distribution calculation' : 'Confirm independent period review'} onClose={() => setPendingAction(null)}>
                <CloseDecisionReview period={period} currency={ccy} decision={pendingAction} />
                {actionError && <Note role="alert" style={{ marginTop: 14 }}>{actionError}</Note>}
                <Note style={{ margin: '12px 0' }}>{pendingAction === 'book' ? 'This records real economic events through the existing posting engine. It is not a wire instruction. Review bank evidence and allocations before confirming.' : pendingAction === 'finalize' ? `This approves the calculation. It does not send cash or post a lender receipt.${readiness?.fourEyes?.value === 'REQUIRED' ? ' A different reviewer is required by tenant policy.' : ''}` : 'Your review is recorded under your signed-in identity. A checker counter-signature freezes this period’s evidence.'}</Note>
                <label style={{ display: 'block', margin: '12px 0' }}><input type="checkbox" checked={actionConfirmed} onChange={e => setActionConfirmed(e.target.checked)} /> I reviewed the evidence and understand this action’s effect.</label>
                <Button $variant="primary" disabled={busy !== null || !actionConfirmed} onClick={() => void confirmAction()}>Confirm {pendingAction === 'book' ? 'lender receipt' : pendingAction === 'finalize' ? 'distribution approval' : pendingAction === 'attest' ? 'maker attestation' : 'checker sign-off'}</Button>
              </CollectionsDrawer>}
            </Work>
          </Layout>
        </>
      )}
    </Shell>
  );
};

export default CollectionsWorkspace;
